import * as React from 'react';

import { DataSourceTable } from 'src/constants/dataSourceTable';

type UseTableCheckingProps = {
  tables: DataSourceTable[];
  setCheckedVgTables: React.Dispatch<React.SetStateAction<DataSourceTable[]>>;
};

type UseTableCheckingResult = {
  handleCheckTable: (tableName: string) => void;
  handleCheckAllInCatalog: (catalog: string, tableNames: string[]) => void;
  handleCheckAllInSchema: (
    catalog: string,
    schema: string,
    tableNames: string[]
  ) => void;
};

export const useTableChecking = ({
  tables,
  setCheckedVgTables,
}: UseTableCheckingProps): UseTableCheckingResult => {
  const handleCheckTable = React.useCallback(
    (tableName: string) => {
      setCheckedVgTables((prev) => {
        const isChecked = prev.some((t) => t.name === tableName);
        if (isChecked) {
          return prev.filter((t) => t.name !== tableName);
        }
        const table = tables.find((t) => t.name === tableName);
        if (table) {
          return [...prev, table];
        }
        return prev;
      });
    },
    [tables, setCheckedVgTables]
  );

  const toggleAllTables = React.useCallback(
    (tableNames: string[]) => {
      setCheckedVgTables((prev) => {
        const prevNames = prev.map((t) => t.name);
        const allChecked = tableNames.every((name) => prevNames.includes(name));

        if (allChecked) {
          return prev.filter((t) => !tableNames.includes(t.name));
        }
        const newTables = tables.filter(
          (t) => tableNames.includes(t.name) && !prevNames.includes(t.name)
        );
        return [...prev, ...newTables];
      });
    },
    [tables, setCheckedVgTables]
  );

  // Note: catalog/schema params are part of the callback interface for VirtualGraphTableTree
  // but not used here since we only need table names to toggle selection state
  const handleCheckAllInCatalog = React.useCallback(
    (_catalog: string, tableNames: string[]) => toggleAllTables(tableNames),
    [toggleAllTables]
  );

  const handleCheckAllInSchema = React.useCallback(
    (_catalog: string, _schema: string, tableNames: string[]) =>
      toggleAllTables(tableNames),
    [toggleAllTables]
  );

  return {
    handleCheckTable,
    handleCheckAllInCatalog,
    handleCheckAllInSchema,
  };
};
